/**
 * Membership API Service
 * Handles all membership-related API calls (roles, permissions, etc.)
 */

import { apiClient } from '../client';
import { formatClientErrorDetail, isExpectedAuthWall } from '../errors';

export interface Role {
  id: string;
  name: string;
  /** Role.key — the stable per-tenant identifier (e.g. for RM PRD's managerRoles config), unlike `name` which is just display text. */
  key?: string;
}

export interface GetRolesResponse {
  results?: Role[];
  data?: Role[];
  // Direct array response is also supported
}

export interface MembershipUser {
  id?: string | number; // TenantMembership pk when from API (number)
  uid?: string;
  user_id?: string; // Primary user identifier from API
  user_parent_id?: number | null; // TenantMembership id of manager (for hierarchy)
  name?: string;
  full_name?: string;
  email?: string;
  created_at?: string;
  date_joined?: string;
  role_id?: string;
  is_active?: boolean;
  department?: string | null;
  department_name?: string | null;
  role?: {
    id?: string;
    key?: string;
    name?: string;
  };
  role_name?: string;
}

/** User with hierarchy fields for the User Hierarchy page */
export interface HierarchyUser {
  membershipId: number;
  user_parent_id: number | null;
  name: string;
  email: string;
  role: { name: string } | null;
}

export interface HierarchyAssignment {
  membership_id: number;
  parent_membership_id: number | null;
}

export interface GetUsersResponse {
  results?: MembershipUser[];
  count?: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  is_active?: boolean;
  created_at: string;
  role_id: string;
  department?: string;
  role: {
    name: string;
  } | null;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isRoleArray(value: unknown): value is Role[] {
  return Array.isArray(value);
}

function isMembershipUserArray(value: unknown): value is MembershipUser[] {
  return Array.isArray(value);
}

function extractRoles(payload: GetRolesResponse | Role[]): Role[] {
  if (Array.isArray(payload)) return payload;
  if (!isObjectRecord(payload)) return [];
  if (isRoleArray(payload.results)) return payload.results;
  if (isRoleArray(payload.data)) return payload.data;
  return [];
}

function toRoleInfo(user: MembershipUser): { name: string } | null {
  if (user.role?.name) return { name: user.role.name };
  if (user.role?.key) return { name: user.role.key };
  if (user.role_name) return { name: user.role_name };
  return null;
}

function getMembershipId(user: MembershipUser): number | null {
  if (typeof user.id === 'number') return user.id;
  if (typeof user.id === 'string') {
    const parsed = Number(user.id);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function extractMembershipUsers(payload: unknown): MembershipUser[] {
  // Canonical backend contract for GET /membership/users is:
  // { count: number, results: MembershipUser[] }.
  // Keep only a minimal array fallback for backward compatibility.
  if (isMembershipUserArray(payload)) return payload;
  if (!isObjectRecord(payload)) return [];
  if (isMembershipUserArray(payload.results)) return payload.results;

  return [];
}

/** Shared GET /membership/roles/ — tenant resolved from JWT on the backend. */
async function fetchMembershipRoles(params?: Record<string, string>): Promise<Role[]> {
  const response = await apiClient.get<GetRolesResponse | Role[]>('/membership/roles/', {
    ...(params ? { params } : {}),
  });
  return extractRoles(response.data);
}

/** Normalize POST /membership/roles/ response shapes to a Role. */
function parseCreatedRolePayload(responseData: unknown): Role {
  if (responseData && typeof responseData === 'object') {
    if ('data' in responseData && responseData.data) {
      return responseData.data as Role;
    }
    if (
      'results' in responseData &&
      Array.isArray(responseData.results) &&
      responseData.results.length > 0
    ) {
      return responseData.results[0] as Role;
    }
    if ('id' in responseData && 'name' in responseData) {
      return responseData as Role;
    }
  }
  console.warn('Unexpected response format from create role endpoint, using fallback');
  return { id: '', name: '' };
}

/** User shape for lead-assignment workflows (keeps both membership id and auth UUID). */
export interface AssignmentUser {
  id: string; // TenantMembership id
  userUuid: string; // auth.users UUID used for assigned_to
  name: string;
  email: string;
  roleName: string;
}

export interface MyMembershipResponse {
  role_key: string | null;
  role_name: string | null;
  role_id: string | null;
  tenant_id: string | null;
  tenant_slug?: string | null;
  is_active?: boolean;
  department?: string | null;
  error?: string;
  /** Current user's TenantMembership id (integer). Used for team_lead when user_parent_id is null. */
  tenant_membership_id?: number | null;
  /** Parent TenantMembership id (user_parent_id_id). If null, use tenant_membership_id as team_lead. */
  user_parent_id?: number | null;
}

/**
 * Membership API Service
 */
export const membershipService = {
  /**
   * Get all roles for the current tenant
   * Uses the membership/roles endpoint
   * 
   * @returns Promise with array of roles
   */
  async getRoles(): Promise<Role[]> {
    try {
      return await fetchMembershipRoles();
    } catch (error: unknown) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  /**
   * Get the public role for a tenant (key=public).
   * Uses the membership/roles endpoint. Returns null on any error or if not found.
   * Use this instead of the non-existent api/authz/roles endpoint.
   * Tenant is resolved from the Supabase JWT (user_data) on the backend.
   *
   * @returns Promise with the public role or null
   */
  async getPublicRole(): Promise<Role | null> {
    try {
      const roles = await fetchMembershipRoles({ key: 'public' });
      return roles.length > 0 ? roles[0] : null;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : error;
      console.warn('Failed to fetch public role via membership API:', message);
      return null;
    }
  },

  /**
   * Get all users for the current tenant
   * Uses the membership/users endpoint
   *
   * @returns Promise with array of users
   */
  async getUsers(): Promise<User[]> {
    try {
      const response = await apiClient.get<GetUsersResponse | MembershipUser[]>('/membership/users/');
      const usersData = extractMembershipUsers(response.data);
      
      // Transform the data to match the User interface.
      // For routing rules we key by TenantMembership id, so prefer id (membership pk) when present.
      const transformedUsers: User[] = usersData.map((user: MembershipUser, index: number) => {
        const rawId =
          user.id ??
          user.user_id ??
          user.uid ??
          `temp-${index}-${Math.random().toString(36).substring(2, 15)}`;

        return {
          id: String(rawId),
          name: user.name || user.full_name || 'Unnamed User',
          email: user.email || 'No Email',
          is_active: user.is_active !== false,
          role_id: user.role_id || user.role?.id || '',
          created_at: user.created_at || user.date_joined || new Date().toISOString(),
          department: user.department ?? user.department_name ?? undefined,
          role: toRoleInfo(user),
        };
      });
      
      // Sort by created_at descending (newest first)
      return transformedUsers.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    } catch (error: unknown) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get active users for lead assignment.
   * Returns both membership id and auth UUID (user_id/uid) required by lead assigned_to.
   */
  async getActiveUsersForAssignment(): Promise<AssignmentUser[]> {
    try {
      const response = await apiClient.get<GetUsersResponse | MembershipUser[]>('/membership/users/');
      const responseData = response.data;

      let usersData: MembershipUser[] = [];
      if (Array.isArray(responseData)) {
        usersData = responseData;
      } else if (responseData && typeof responseData === 'object') {
        if ('results' in responseData && Array.isArray(responseData.results)) {
          usersData = responseData.results;
        } else if ('data' in responseData && Array.isArray(responseData.data)) {
          usersData = responseData.data;
        }
      }

      return usersData
        .filter((u) => u.is_active !== false)
        .map((u, index) => ({
          id: String(u.id ?? `idx-${index}`),
          userUuid: String(u.user_id || u.uid || ''),
          name: u.name || u.full_name || 'Unnamed User',
          email: u.email || 'No email',
          roleName: u.role?.name || u.role?.key || u.role_name || '',
        }))
        .filter((u) => u.userUuid.length > 0);
    } catch (error: any) {
      console.error('Error fetching active users for assignment:', error);
      throw error;
    }
  },

  /**
   * Create a new role for the current tenant
   * Uses the membership/roles endpoint (POST)
   * 
   * @param key - The role key (e.g., "HM")
   * @param name - The role name (e.g., "Head of Management")
   * @returns Promise with the created role
   */
  async createRole(key: string, name: string): Promise<Role> {
    try {
      const response = await apiClient.post<Role | { data: Role } | { id: string; name: string } | { results: Role[] }>(
        '/membership/roles/',
        {
          key,
          name,
        }
      );

      return parseCreatedRolePayload(response.data);
    } catch (error: unknown) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  /**
   * Get all users with hierarchy fields (membershipId, user_parent_id) for the User Hierarchy page.
   * Uses the same GET /membership/users endpoint (backend includes id and user_parent_id).
   */
  async getUsersForHierarchy(): Promise<HierarchyUser[]> {
    const response = await apiClient.get<GetUsersResponse | MembershipUser[]>('/membership/users/');
    const usersData = extractMembershipUsers(response.data);

    return usersData
      .map((u) => ({ user: u, membershipId: getMembershipId(u) }))
      .filter(
        (item): item is { user: MembershipUser; membershipId: number } =>
          item.membershipId != null
      )
      .map((u) => ({
        membershipId: u.membershipId,
        user_parent_id: u.user.user_parent_id ?? null,
        name: u.user.name || u.user.full_name || 'Unnamed User',
        email: u.user.email || 'No Email',
        role: toRoleInfo(u.user),
      }));
  },

  /**
   * Update user reporting hierarchy (who reports to whom).
   * Restricted to GM/ASM on the backend.
   */
  async updateUserHierarchy(
    assignments: HierarchyAssignment[]
  ): Promise<{ count: number }> {
    const response = await apiClient.patch<{ count: number }>(
      '/membership/users/hierarchy/',
      { assignments }
    );
    return response.data;
  },

  /**
   * Get current user's membership information (tenant_id, role_id) from backend.
   * Used as fallback when JWT doesn't contain user_data claims.
   * This is the source of truth for user's tenant and role.
   * Tenant is resolved from the Supabase JWT on the backend.
   *
   * @returns Promise with membership info (tenant_id, role_id, role_key, etc.) or null if not found
   */
  async getMyMembership(tenantSlug?: string): Promise<MyMembershipResponse | null> {
    try {
      if (import.meta.env.DEV) {
        console.log('[membershipService] getMyMembership: GET /membership/me/role/');
      }

      const response = await apiClient.get<MyMembershipResponse>('/membership/me/role/', {
        ...(tenantSlug
          ? { headers: { 'X-Tenant-Slug': tenantSlug } as Record<string, string> }
          : {}),
      });

      if (import.meta.env.DEV) {
        const d = response.data;
        console.log(
          `[membershipService] getMyMembership: response role_id=${d?.role_id ?? 'n/a'} tenant_id=${d?.tenant_id ?? 'n/a'} role_key=${d?.role_key ?? 'n/a'} backendError=${d?.error ?? 'none'}`
        );
      }

      if (response.data?.error) {
        console.warn(`[membershipService] getMyMembership: backend error field: ${response.data.error}`);
        return null;
      }

      if (!response.data?.role_id || !response.data?.tenant_id) {
        console.warn('[membershipService] getMyMembership: missing role_id or tenant_id in response');
        return null;
      }

      if (import.meta.env.DEV) {
        console.log(
          `[membershipService] getMyMembership: OK tenant=${response.data.tenant_id} role=${response.data.role_id} role_key=${response.data.role_key ?? 'n/a'}`
        );
      }

      return response.data;
    } catch (error: unknown) {
      if (isExpectedAuthWall(error)) {
        // Stale session / no tenant permission — expected; do not console.error (Sentry captureConsoleIntegration)
        console.warn(
          `[membershipService] getMyMembership: not available (auth): ${formatClientErrorDetail(error)}`
        );
        return null;
      }
      console.error(
        `[membershipService] getMyMembership: unexpected failure: ${formatClientErrorDetail(error)}`
      );
      return null;
    }
  },

  /**
   * Generate a spoofing JWT for a specific tenant membership.
   * Used by admin tools to temporarily act as another user.
   */
  async spoofUserToken(
    membershipId: string
  ): Promise<{ token: string; email: string; name?: string; tenant_id?: string }> {
    const response = await apiClient.post<{
      token: string;
      email: string;
      name?: string;
      tenant_id?: string;
    }>(`/membership/users/${membershipId}/spoof-token/`, {});
    return response.data;
  },
};

